import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  AnalyticsPageView,
  AnalyticsProvider,
} from '@nerdwallet/seo-analytics';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';
import Results from '../../components/currency/results/results';
import {
  MCControlPanel,
  MCResultsPanel,
  MORTGAGE_PAYMENT_CALCULATOR,
  MORTGAGE_PAYMENT,
} from '../../components/mortgage-calculator/shared';
import ToolLinkCards from '../../components/currency/tool-link-cards/tool-link-cards';
import type {
  MortgageCalculator,
  MortgageValidations,
} from '../../components/mortgage-calculator/shared';
import toolLinkConfig from '../../components/mortgage-calculator/shared/tool-link-config';
import { useUrlRecordContext } from '../../components/url-record';
import SegmentImpression from '../../lib/analytics/segment-impression';

// Lazy Load Components
const LenderCards = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-lender-cards' */ '../../components/currency/lender-cards/lender-cards'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-related-articles' */ '../../components/currency/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

export type MCResultsProps = {
  dispatch: (a: any) => void;
  experiments?: any;
  mortgageCalculator?: MortgageCalculator;
  validations: MortgageValidations;
  impressions?: any;
  location?: Record<string, any>;
  history?: Record<string, any>;
};

const MPCResults = (props: MCResultsProps): JSX.Element => {
  const { mortgageCalculator, impressions, validations, location, history } =
    props;

  const { title, synopsis } = useUrlRecordContext();

  const data = {
    ...(mortgageCalculator || {}),
    validations,
    impressions,
  };

  return (
    <AnalyticsProvider>
      <AnalyticsPageView name="mortgages_payment_calculator">
        <Impression
          eventType="page_impression"
          entityName="mortgages_payment_calculator"
        >
          <SegmentImpression
            eventType="form_viewed"
            attributes={{
              entity_name: 'mortgage_payment_calculator',
              form_name: 'mortgage_payment_calculator',
            }}
          >
            <Results
              {..._.assign({}, props, {
                className: 'mc-app',
                controlPanel: (
                  <MCControlPanel mortgageCalculator={MORTGAGE_PAYMENT} />
                ),
                data,
                heading: title,
                showSummaryTable: true,
                subcopy: synopsis,
                lenders: (
                  <LenderCards
                    trackingFormName="mpc"
                    namedQuery="mortgages_offers_mpc"
                    analyticsTrackingConfig={{
                      entityName: 'mortgages_calculator_offer',
                      sectionName: 'mortgages_calculator_offer',
                      sectionPosition: 1,
                    }}
                  />
                ),
                resultsPanel: (
                  <MCResultsPanel
                    // @ts-ignore - ignore error
                    location={location}
                    history={history}
                    trackingFormName="mpc-results"
                    mortgageCalculator={MORTGAGE_PAYMENT}
                  />
                ),
                methodology: (
                  <Methodology
                    trackingFormName="mpc-methodology"
                    impressionTracking
                  />
                ),
                relatedArticles: (
                  <RelatedArticles
                    entityName={
                      MORTGAGE_PAYMENT_CALCULATOR.RELATED_ARTICLES.entityName
                    }
                    sectionName={
                      MORTGAGE_PAYMENT_CALCULATOR.RELATED_ARTICLES.sectionName
                    }
                    sectionPosition={
                      MORTGAGE_PAYMENT_CALCULATOR.RELATED_ARTICLES
                        .sectionPosition
                    }
                    articles={
                      MORTGAGE_PAYMENT_CALCULATOR.RELATED_ARTICLES.articles
                    }
                  />
                ),
                toolLinkCards: (
                  <ToolLinkCards
                    header="Other NerdWallet Resources"
                    cards={toolLinkConfig.cards}
                    impressionTrackingName="mortgages_payment_calculator_tool_card"
                    data={data}
                    calculatorType="mortgage_payment_calculator"
                    analyticsConfig={{
                      sectionName: 'mortgages_payment_calculator_tool_card',
                      sectionPosition: 6,
                    }}
                  />
                ),
              })}
            >
              <div />
            </Results>
          </SegmentImpression>
        </Impression>
      </AnalyticsPageView>
    </AnalyticsProvider>
  );
};

const mapStateToProps = (state) => ({
  mortgageCalculator: state?.mortgageCalculator,
  validations: state?.validations,
  identity: state?.identity,
  impressions: state?.impressions,
});

export default connect(mapStateToProps)(MPCResults);
